import { useState } from "preact/hooks"
import { useDropzone } from "react-dropzone"
import { Button } from "@/components/ui/button"
import { toast } from "@/components/ui/use-toast"
import { FileText, Upload, X } from "lucide-react"

interface PromotionImportResponse {
  calendars_created: number
  calendars_reused: number
  inserted: number
  updated: number
  unchanged: number
  failed: number
  parse_errors: number
  skipped_excluded_types: number
  duplicate_line_key_count: number
  missing_line_key_count: number
  duplicate_remote_promo_id_count: number
  errors: string[]
}

const REQUIRED_COLUMNS = [
  "Customer Name",
  "First Receiver",
  "Promotion Name",
  "Promotion Type",
  "Line Key",
  "Product Name",
]

function getSelectedFileLabel(file: File | null): string {
  return file ? `${file.name} (${Math.round(file.size / 1024)} KB)` : "No CSV selected"
}

function getUploadSummary(result: PromotionImportResponse): string {
  return `${result.inserted} new, ${result.updated} updated, ${result.unchanged} unchanged, ${result.failed} failed`
}

export function PromotionUpload() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [lastResult, setLastResult] = useState<PromotionImportResponse | null>(null)

  function handleDrop(files: File[]) {
    setSelectedFile(files[0] || null)
  }

  function handleClearSelectedFile() {
    setSelectedFile(null)
  }

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: handleDrop,
    multiple: false,
    noClick: true,
    noKeyboard: true,
    accept: {
      "text/csv": [".csv"],
    },
  })

  async function handleUpload() {
    if (!selectedFile) {
      toast({
        title: "No file selected",
        description: "Choose a promotion breakdown CSV to upload.",
        variant: "destructive",
      })
      return
    }

    setUploading(true)
    try {
      const formData = new FormData()
      formData.append("file", selectedFile)

      const response = await fetch("/api/organization/vividly-promotions/upload", {
        method: "POST",
        credentials: "same-origin",
        body: formData,
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(errorText || "Failed to upload promotion CSV")
      }

      const payload = await response.json()
      const result: PromotionImportResponse = payload.data || payload
      setLastResult(result)
      toast({
        title: "Promotion import complete",
        description: getUploadSummary(result),
        variant: result.failed > 0 ? "destructive" : "default",
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to upload promotion CSV"
      toast({
        title: "Upload failed",
        description: message,
        variant: "destructive",
      })
    } finally {
      setUploading(false)
    }
  }

  return (
    <div class="space-y-4">
      <div class="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900 space-y-2">
        <p class="font-medium">CSV format</p>
        <p>
          Upload the <strong>promotion breakdown</strong> export (one row per promotion line, with product detail).
          Do not use the promotion-level / “whole” export — that file is missing columns this import requires.
        </p>
        <p>
          Export from your TPM as CSV. Each row should represent a single promotion line. Promotions are matched on
          re-upload using the <code class="text-xs">Line Key</code> column — weekly full exports are safe; only new
          lines are inserted and changed lines are updated.
        </p>
        <p class="font-medium mb-1">Required columns</p>
        <p class="text-xs">{REQUIRED_COLUMNS.join(", ")}</p>
        <p class="text-xs text-blue-800">
          Common optional columns: Status, Start Date, End Date, Year, Product Group, Spend Rate, Lump Sum Spend,
          Total Expected Spend, Actual Spend.
        </p>
      </div>

      <div
        {...(getRootProps() as object)}
        class={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
          isDragActive ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-gray-400"
        }`}
      >
        <input {...getInputProps()} />
        <Upload className="mx-auto h-10 w-10 text-gray-400 mb-3" />
        <p class="text-sm font-medium text-gray-900 mb-1">
          {isDragActive ? "Drop CSV here" : "Drag and drop promotion breakdown CSV"}
        </p>
        <p class="text-xs text-gray-500 mb-3">Only `.csv` files are supported</p>
        <Button variant="outline" onClick={open} disabled={uploading}>
          Select CSV
        </Button>
      </div>

      <div class="flex items-center justify-between rounded-md border border-gray-200 p-3">
        <div class="flex items-center gap-2 text-sm">
          <FileText className="h-4 w-4 text-gray-500" />
          <span>{getSelectedFileLabel(selectedFile)}</span>
        </div>
        <div class="flex items-center gap-2">
          <Button variant="outline" onClick={handleClearSelectedFile} disabled={!selectedFile || uploading}>
            <X className="h-4 w-4 mr-1" />
            Clear
          </Button>
          <Button onClick={handleUpload} disabled={!selectedFile || uploading}>
            {uploading ? "Uploading..." : "Upload Promotions CSV"}
          </Button>
        </div>
      </div>

      {lastResult && (
        <div class="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm space-y-1">
          <div>New promotions: {lastResult.inserted}</div>
          <div>Updated: {lastResult.updated}</div>
          <div>Unchanged (already imported): {lastResult.unchanged}</div>
          <div>Failed: {lastResult.failed}</div>
          <div>Calendars created: {lastResult.calendars_created}</div>
          <div>Calendars reused: {lastResult.calendars_reused}</div>
          {lastResult.missing_line_key_count > 0 && (
            <div class="text-amber-700">
              {lastResult.missing_line_key_count} rows missing Line Key (cannot dedupe on re-upload)
            </div>
          )}
          {lastResult.errors.length > 0 && (
            <div class="mt-2 max-h-32 overflow-y-auto text-xs text-red-700">
              {lastResult.errors.slice(0, 10).map((err: string, idx: number) => (
                <div key={`${idx}-${err}`}>{err}</div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
